<template>
    <q-page :padding="$q.screen.gt.md">
        <alert display-page="landing" dir="ltr"/>

        <vue-trustpilot
            identifier="list"
            v-show="!notCanceledOrdersCount"
            class="flex flex-center q-ma-lg"
        />

        <div class="q-pa-sm flex flex-center">
            <q-stepper
                v-model="step"
                flat
                bordered
                ref="stepper"
                color="primary"
                animated
                keep-alive
                :contracted="$q.screen.lt.sm"
                done-color="green"
                active-color="primary"
                inactive-color="grey-6"
                header-nav
                class="q-mb-xl"
                style="max-width: 700px"
                @transition="stepChanged"
            >
                <q-step
                    v-if="isAdmin"
                    :name="0"
                    title="User"
                    icon="fa fa-users"
                    :done="step > 0"
                    :header-nav="step > 0 && step !== 4"
                >
                    <div class="row q-col-gutter-lg">
                        <div class="col-12">
                            <q-select
                                v-model="transfer.user_id"
                                :options="users"
                                use-input
                                hide-selected
                                fill-input
                                input-debounce="500"
                                option-value="id"
                                option-label="name"
                                emit-value
                                map-options
                                clearable
                                :label="$t('transfers.user')"
                                :error="transfer.errors.has('user_id')"
                                color="primary"
                                @filter="filterUsers"
                            >
                                <template v-slot:option="scope">
                                    <q-item
                                        v-bind="scope.itemProps"
                                        v-on="scope.itemEvents"
                                        style="border-bottom:1px solid #999"
                                        @click="grabUser(scope.opt)"
                                    >
                                        <q-item-section top>
                                            <q-item-label lines="1">
                                                <span class="text-green">{{ scope.opt.id }} - </span>
                                                <span
                                                    class="text-weight-medium text-uppercase">{{ scope.opt.name }}</span>
                                            </q-item-label>
                                            <q-item-label caption lines="1">
                                                {{ scope.opt.email }}
                                            </q-item-label>
                                            <q-item-label lines="1" class="text-body2 text-weight-bold text-primary">
                                                <span>{{ scope.opt.mobile }}</span>
                                            </q-item-label>
                                        </q-item-section>
                                    </q-item>
                                </template>
                                <template v-slot:no-option>
                                    <q-item>
                                        <q-item-section class="text-grey">
                                            {{$t('transfers.no_user_found')}}!
                                        </q-item-section>
                                    </q-item>
                                </template>
                            </q-select>
                        </div>
                        <div class="col-12">
                            <div class="q-gutter-sm">
                                <q-checkbox
                                    left-label
                                    v-model="transfer.bypass"
                                    :label="$t('transfers.bypass_transfer_amount_limit')"
                                    color="primary"/>
                            </div>
                        </div>
                    </div>
                    <q-stepper-navigation align="right">
                        <q-btn @click="continueByUser()"
                               color="primary"
                               :label="$t('transfers.send_money_steps.continue')"
                               :disabled="!transfer.user_id"
                               :loading="rewards_loading"
                        />
                    </q-stepper-navigation>
                </q-step>

                <q-step
                    :name="1"
                    :title="$t('transfers.send_money_steps.amount')"
                    icon="fa fa-calculator"
                    :done="step > 1"
                    :header-nav="step > 1 && step !== 4"
                >
                    <div class="row q-col-gutter-lg">
                        <div class="col-xs-12 flex flex-center">
                            <calculator
                                :user-rewards="userRewards"
                                :is-introduced="isUserIntroduced"
                                :user-currency="userCurrency"
                                :account-age="userAccountAge"
                                :error="calculationError"
                                :reward-min-transfer-amount="reward_min_transfer_amount"
                                @result="makeTransferRecord"
                            />
                        </div>
                    </div>
                    <q-stepper-navigation class="flex" :class="isAdmin ? 'justify-between' : 'justify-end'">
                        <q-btn
                            outline
                            @click="$refs.stepper.previous()"
                            color="primary"
                            :label="$t('back')"
                            class="q-mr-sm"
                            v-if="isAdmin"
                        />
                        <q-btn @click="checkIfAwaitingOrderExists"
                               color="primary"
                               :label="$t('transfers.send_money_steps.continue')"
                               :loading="loading || recipient_loading"
                               :disabled="!(transfer.origin_amount && transfer.dest_amount) || errored"/>
                    </q-stepper-navigation>
                </q-step>

                <q-step
                    :name="2"
                    :title="$t('transfers.send_money_steps.recipient')"
                    icon="fa fa-users"
                    :done="step > 2"
                    :header-nav="step > 2 && step !== 4"
                >
                    <div class="row q-col-gutter-lg">
                        <div class="text-h6 col-12">
                            {{ cryptoExchangeMode ? $t('transfers.recipient_s_details') : $t('transfers.who_are_you_sending_money_to')}}
                        </div>
                        <div class="col-12" style="max-width: 450px">
                            <q-select
                                outlined
                                v-model="invoice.bank_account_id"
                                :options="filtered_recipients"
                                :label="$t('transfers.select_your_recipient')"
                                option-value="bank_account_id"
                                option-label="name"
                                map-options
                                emit-value
                                style="width: 100%"
                                :loading="recipient_loading"
                                color="primary"
                                ref="recipient"
                            >
                                <template v-slot:option="scope">
                                    <q-item
                                        v-bind="scope.itemProps"
                                        v-on="scope.itemEvents"
                                        style="border-bottom:1px solid #999"
                                    >
                                        <q-item-section v-if="scope.index + 1 === filtered_recipients.length"
                                                        @click="displayRecipientModal">
                                            <q-item-label
                                                lines="1"
                                                class="text-bold text-primary text-body1"
                                            >
                                                <q-icon name="fa fa-plus" color="primary" size="xs"/>
                                                {{ scope.opt.name }}
                                            </q-item-label>
                                        </q-item-section>

                                        <q-item-section v-else>
                                            <q-item-label lines="1" class="text-bold">{{ scope.opt.name }}</q-item-label>
                                            <q-item-label caption>{{ scope.opt.bank }}</q-item-label>
                                            <q-item-label class="text-black">{{ scope.opt.account_number }}
                                            </q-item-label>
                                        </q-item-section>
                                    </q-item>
                                </template>
                            </q-select> <!-- YOUR RECIPIENT -->
                            <div>
                                <q-btn
                                    flat
                                    :label="$t('transfers.edit_recipient')"
                                    color="primary"
                                    icon="fa fa-edit"
                                    @click="displayEditRecipientModal = true"
                                />
                            </div>
                        </div>
                        <div class="col-12" v-if="cryptoExchangeMode" :title="invoice.reason = 'crypto'"></div>
                        <div class="col-12" v-else>
                            <p class="text-h6">{{$t('transfers.reason_for_transfer')}}:</p>
                            <q-list dense>
                                <q-item tag="label" v-for="reason in transfer_reasons" :key="reason.name">
                                    <q-item-section avatar>
                                        <q-radio v-model="invoice.reason" :val="reason.name" color="primary"/>
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label>{{reason.label}}</q-item-label>
                                    </q-item-section>
                                </q-item>

                                <q-item>
                                    <q-item-section>
                                        <q-input
                                            v-model="invoice.reason_text"
                                            autogrow
                                            v-if="invoice.reason === 'other'"
                                            :placeholder="$t('transfers.type_your_reason_here')"
                                            color="primary"
                                        />
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </div> <!-- REASONS -->
                    </div>
                    <div class="row q-col-gutter-lg" v-if="! isUserFullyVerifiedOrReviewed">
                        <div class="col">
                            <q-item class="q-pa-none">
                                <q-item-section avatar>
                                    <q-icon name="fa fa-exclamation-triangle" color="warning"/>
                                </q-item-section>

                                <q-item-section class="text-orange">{{$t('transfers.we_need_to_complete_your_verification_before_receiving_orders')}}</q-item-section>
                            </q-item>
                        </div>
                    </div>

                    <q-stepper-navigation class="flex justify-between">
                        <q-btn
                            outline
                            @click="$refs.stepper.previous()"
                            color="primary"
                            :label="$t('back')"
                            class="q-mr-sm"
                        />
                        <q-btn
                            @click="saveTemporarily"
                            color="primary"
                            :label="$t('transfers.send_money_steps.continue')"
                            :disabled="!(invoice.bank_account_id && invoice.reason && (invoice.reason === 'other' ? invoice.reason_text : true)) || ! isUserFullyVerifiedOrReviewed"
                            :loading="loading"
                            v-if="isUserFullyVerifiedOrReviewed"
                        />
                        <q-btn
                            @click="$router.push({name: 'account', params:{tab: 'verification'}})"
                            color="primary"
                            :label="$t('transfers.complete_verification')"
                            v-else
                        />
                    </q-stepper-navigation>
                </q-step>

                <q-step
                    :name="3"
                    :title="$t('transfers.send_money_steps.review')"
                    icon="fa fa-search-dollar"
                    :done="step > 3"
                    :header-nav="step > 3 && step !== 4"
                >
                    <p class="text-h5">
                        {{$t('transfers.review_and_confirm')}}
                    </p>
                    <div class="row q-col-gutter-lg" v-if="invoice.bank_account_id">
                        <div class="col-12">
                            <review-amount :decimals="transfer.decimals" :transfer="invoice" :recipient="recipient" @edit="step = 1"/>
                        </div>
                        <div class="col-12">
                            <review-recipient :recipient="recipient" @change="step = 2"/>
                        </div>
                        <div class="col-12">
                            <transfer-time v-model="invoice" :transfer-time="transfer_time" :key="renderKey"/>
                        </div>
                        <div class="col-12">
                            <q-input
                                :label="$t('transfers.table_titles.note')"
                                v-model="invoice.notes"
                                autogrow
                                outlined
                                :hint="$t('transfers.example_please_pay_by_sunday')"
                                maxlength="190"
                                counter
                            />
                            <p v-if="invoice.from_currency === 'RLS' && currentUser.set" class="q-pt-sm">
                                <q-icon name="fa fa-warning" color="orange"/>
                                {{$t('transfers.if_account_owner_name_is_not', {first_name: this.currentUser.first_name, last_name: this.currentUser.last_name})}}
                            </p>
                        </div>
                    </div>

                    <q-stepper-navigation class="flex justify-between">
                        <q-btn
                            outline
                            @click="$refs.stepper.previous()"
                            color="primary"
                            :label="$t('back')"
                            class="q-mr-sm"
                        />
                        <q-btn
                            @click="displayMakeSureModal = true"
                            color="primary"
                            :label="$t('transfers.submit_pay_now')"
                            :loading="loading"/>
                    </q-stepper-navigation>
                </q-step>
            </q-stepper>
        </div>

        <recipient-modal
            v-model="showRecipientDialog"
            :user="selectedUser"
            :key="recipientModalKey"
            @created="addRecipient"
            :dest-currency="transfer.to_currency"
            side="send-money"
        />

        <open-order-modal
            v-model="displayOpenOrderModal"
            @start="startTransfer"
            @pay="$router.push({ name: 'transfers.history'})"
        />

        <edit-recipient-modal
            v-model="displayEditRecipientModal"
            @do="$router.push({ name: 'recipients.my'})"
        />

        <make-sure-modal
            v-model="displayMakeSureModal"
            :user-full-name="current_user_full_name"
            :loading="loading"
            @confirm="confirmInvoice()"
        />

        <transfer-limit-error-modal
            v-model="displayTransferLimitModal"
            :error-message="calculationError.has('origin_amount') ? calculationError.get('origin_amount') : ''"
            @documents="$router.push({name: 'account', params:{tab: 'verification'}})"
            @guide="displayAcceptedProofGuideModal = true"
        />

        <order-limit-dialog
            v-model="displayOrderLimitDialog"
            :error-message="calculationError.has('origin_amount') ? calculationError.get('origin_amount') : ''"
        />

        <guide-modal v-model="displayAcceptedProofGuideModal" />

        <address-proof-dialog
            v-model="displayAddressProofDialog"
            :address="currentUser.address"
            :user-id="currentUser.uid"
            :country="currentUser.country.acronym"
            :mobile="currentUser.mobile"
            v-if="isUserVerified && Object.keys(currentUser.country).length"
            @updated="addressProofUpdated"
        />

    </q-page>
</template>
<script>

import {mapActions, mapGetters} from 'vuex'
import Error from "src/utils/Errors"
import Transfer from "../models/Transfer"
import User from "Modules/User/resources/models/User"
import Alert from 'Modules/Alert/resources/components/Alert'
import Recipient from 'Modules/Recipient/resources/models/Recipient'
import PaymentInfo from '../components/history/PaymentInfo.vue'
import ReviewAmount from '../components/ReviewAmount.vue'
import TransferTime from '../components/TransferTime.vue'
import ReviewRecipient from '../components/ReviewRecipient.vue'
import MakeSureModal from './send-money-dialogs/MakeSureModal.vue'
import OpenOrderModal from './send-money-dialogs/OpenOrderModal.vue'
import EditRecipientModal from './send-money-dialogs/EditRecipientModal.vue'
import GuideModal from 'Modules/User/resources/pages/account/GuideModal.vue'
import Calculator from 'Modules/Ramp/resources/components/calculator/Calculator.vue'
import RecipientModal from 'Modules/Recipient/resources/components/RecipientModal.vue'
import TransferLimitErrorModal from './send-money-dialogs/TransferLimitErrorModal.vue'
import OrderLimitDialog from "./send-money-dialogs/OrderLimitDialog.vue";
import AddressProofDialog from "Modules/User/resources/components/AddressProofDialog.vue"

export default {
    name: 'SendMoney',
    props: {
        isAdmin: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters(['getVerificationState', 'currentUser', 'isUserVerified', 'notCanceledOrdersCount']),
        isUserFullyVerifiedOrReviewed() {
            return ['verified', 'reviewed'].includes(this.getVerificationState('identity')) && this.getVerificationState('email') === 'verified'
        },
        dest_currency() {
            return this.transfer.to_currency
        },
        isUserSet() {
            return this.currentUser.set
        },
        userCountry() {
            return this.currentUser.set ? this.currentUser.country : []
        },
        userCurrency() {
            return this.userCountry ? this.userCountry.currency_acronym : 'NZD'
        },
        userRewards() {
            return (this.isUserSet && !this.selectedUser.id) ? this.currentUser.rewards : this.selectedUser.rewards
        },
        reward_min_transfer_amount() {
            return this.userCountry.reward_min_transfer_amount || 0
        },
        isUserIntroduced() {
            return (this.currentUser.set && this.currentUser.introduced) || (this.selectedUser.introduced)
        },
        userAccountAge() {
            return this.currentUser.set ? this.currentUser.account_age : this.selectedUser.account_age
        },
        filtered_recipients() {
            let recipients = this.all_recipients.filter(recipient => recipient.currency === this.transfer.to_currency && !recipient.disabled)
            recipients.push({name: this.$t('transfers.add_a_new_recipient'), bank_account_id: undefined})

            return recipients
        },
        recipient() {
            return this.filtered_recipients.find(recipient => recipient.bank_account_id === this.invoice.bank_account_id)
        },
        current_user_full_name() {
            return this.currentUser.first_name + (this.currentUser.middle_name || '') + ' ' + this.currentUser.last_name
        },
        cryptoExchangeMode() {
            return (this.transfer.from_currency === 'USDT') || (this.transfer.to_currency === 'USDT')
        }
    },
    data() {
        return {
            step: this.isAdmin ? 0 : 1,
            loading: false,
            recipient_loading: false,
            rewards_loading: false,
            showRecipientDialog: false,
            recipientModalKey: 1,
            displayMakeSureModal: false,
            displayEditRecipientModal: false,
            displayTransferLimitModal: false,
            displayOrderLimitDialog: false,
            displayAcceptedProofGuideModal: false,
            displayAddressProofDialog: false,
            all_recipients: [],
            reasonOption: undefined,
            transfer: this.initTransfer(),
            invoice: this.initTransfer(),
            payment: {
                particulars: '',
                reference: '',
                amount: 0,
                our_bank_account: '',
                type: '',
            },
            users: [],
            selectedUser: {
                id: undefined,
                name: undefined,
                email: undefined,
                mobile: undefined,
                rewards: {
                    unused: [],
                    welcome_reward: 0
                }
            },
            errored: false,
            transfer_time: '',
            calculationError: new Error(),
            renderKey: 0,
            displayOpenOrderModal: false,
            transfer_reasons: [
                {name: 'gift_or_support_family_friends', label: this.$t('transfers.gift_or_support_family_friends')},
                {name: 'travel_holiday_immigration', label: this.$t('transfers.travel_holiday_immigration')},
                {name: 'real_estate_relocation_rent', label: this.$t('transfers.real_estate_relocation_rent')},
                {name: 'university_tuition_fees', label: this.$t('transfers.university_tuition_fees')},
                {name: 'pay_salary', label: this.$t('transfers.pay_salary')},
                {name: 'transfer_business_funds', label: this.$t('transfers.transfer_business_funds')},
                {name: 'charity', label: this.$t('transfers.charity')},
                {name: 'other', label: this.$t('transfers.other') + ' ...'},
            ]
        }
    },
    methods: {
        ...mapActions(['use_rewards', 'set_menu_badge', 'set_exchange_rates']),
        grabUser(user) {
            this.rewards_loading = true
            new User({id: user.id}).rewards((data) => {
                user.rewards = data
                this.selectedUser = user
                this.rewards_loading = false
            }).catch((error) => {
                this.rewards_loading = false
            })
        },
        initTransfer() {
            return new Transfer({
                user_id: undefined,
                bypass: this.isAdmin ? false : undefined,
                bank_account_id: undefined,
                reason: this.cryptoExchangeMode ? 'Crypto' : undefined,
                reason_text: '',
                from_currency: undefined,
                to_currency: undefined,
                to_currency_symbol: undefined,
                from_currency_symbol: undefined,
                discount_type: undefined,
                calculation_side: 'dest',
                decimals: 2,
                rate: 1,
                current_rate: 1,
                origin_amount: 1000,
                dest_amount: 0,
                type: undefined,
                notes: null
            })
        },
        continueByUser() {
            this.fetchRecipients()
            this.$refs.stepper.next()
        },
        checkIfAwaitingOrderExists() {
            if (this.currentUser.awaiting_orders) {
                this.displayOpenOrderModal = true
            } else {
                this.startTransfer()
            }
        },
        fetchRecipients() {
            this.recipient_loading = true
            Recipient.all(`${this.transfer.user_id ? `user_id=${this.transfer.user_id}&` : ''}sort=id%7Cdesc&page=1&per_page=16&filter=`, ({data}) => {
                this.recipient_loading = false

                this.all_recipients = data.flatMap((recipient) => {
                    return recipient.bank_accounts.flatMap((bank_account) => {
                        return {
                            bank_account_id: bank_account.id,
                            name: recipient.name,
                            bank: bank_account.bank,
                            account_number: bank_account.number,
                            currency: recipient.currency_acronym,
                            label1: bank_account.info_label1,
                            label2: bank_account.info_label2,
                            notes: bank_account.notes,
                            type: bank_account.type,
                            bank_required: bank_account.bank_required,
                            disabled: bank_account.disabled
                        }
                    })
                })
            })
        },
        fetchRewards() {
            this.rewards_loading = true
            new User({id: this.selectedUser.id}).rewards((data) => {
                this.selectedUser.rewards = data
                this.rewards_loading = false
                this.$refs.stepper.next()
            }).catch((error) => {
                this.rewards_loading = false
            })
        },
        displayRecipientModal() {
            this.recipientModalKey += 1
            this.showRecipientDialog = true
        },
        addRecipient(data) {
            this.all_recipients.splice(0,0,{
                bank_account_id: data.bank_accounts[0].id,
                name: data.name,
                bank: data.bank_accounts[0].bank,
                account_number: data.bank_accounts[0].number,
                currency: data.currency_acronym,
                label1: data.bank_accounts[0].info_label1,
                label2: data.bank_accounts[0].info_label2,
                notes: data.bank_accounts[0].notes,
                type: data.bank_accounts[0].type,
                bank_required: data.bank_accounts[0].bank_required
            })

            if (this.invoice.to_currency === data.currency_acronym) {
                this.invoice.bank_account_id = data.bank_accounts[0].id
            }
        },
        startTransfer() {
            this.recipientModalKey += 1
            this.invoice.fill(this.transfer)
            if (this.transfer.bypass) {
                this.$refs.stepper.next()
                return
            }

            if (!this.transfer.user_id) {
                this.$q.cookies.remove('last_from_currency')
                this.$q.cookies.set('last_from_currency', this.transfer.from_currency)
                this.$q.cookies.remove('last_to_currency')
                this.$q.cookies.set('last_to_currency', this.transfer.to_currency)
            }

            this.loading = true
            new Transfer({
                user_id: this.transfer.user_id,
                origin_amount: this.transfer.origin_amount,
                from_currency: this.transfer.from_currency,
                to_currency: this.transfer.to_currency
            }).validateTransferAmount(() => {
                this.loading = false
                this.$refs.stepper.next()
            }).catch(({data}) => {
                this.loading = false
                if (data.errors.origin_amount && data.errors.origin_amount[0].includes('Sending amount should be')) {
                    this.displayOrderLimitDialog = true
                } else {
                    this.displayTransferLimitModal = true
                }
                this.calculationError.record(data.errors)
            })
        },
        makeTransferRecord(calcResult) {
            if (!calcResult.ramp) {
                return
            }
            this.ignoreError()
            this.transfer.from_currency = calcResult.ramp.from.acronym
            this.transfer.to_currency = calcResult.ramp.to.acronym
            this.transfer.rate = calcResult.rate
            this.transfer.current_rate = calcResult.current_rate
            this.transfer.fees = calcResult.ramp.fees
            this.transfer.referral_earnings = calcResult.discount
            this.transfer.origin_amount = calcResult.origin_amount
            this.transfer.dest_amount = calcResult.dest_amount
            this.transfer.decimals = calcResult.ramp.decimals
            this.transfer.total = calcResult.total
            this.transfer_time = calcResult.transfer_time
            this.transfer.discount_type = calcResult.discount_type
            this.transfer.calculation_side = calcResult.calculation_side
            this.errored = (calcResult.origin_amount < calcResult.ramp.min_transfer_amount) || (calcResult.rate !== calcResult.current_rate && !calcResult.discount_type)
        },
        saveTemporarily() {
            this.loading = true
            this.selectedUser.id = this.transfer.user_id
            this.invoice.make((data) => {
                this.loading = false
                this.$refs.stepper.next()
            }).catch((data) => {
                if (data.status === 422) {
                    this.$q.dialog({
                        title: this.$t('transfers.send_money_steps.exchange_rate_expired'),
                        message: this.$t('transfers.send_money_steps.seems_your_data_is_out_of_date_let_s_try_again'),
                        persistent: true,
                        ok: this.$t('transfers.send_money_steps.start_over'),
                    }).onOk(() => {
                        this.step = 1
                        this.set_exchange_rates(true)
                    })
                } else {
                    this.$q.notify({
                        message: 'Something went wrong!',
                        color: 'negative',
                        icon: 'fa fa-exclamation-triangle',
                        timeout: 5000,
                    })
                }
                this.loading = false
            })
        },
        confirmInvoice() {
            this.loading = true
            this.invoice.confirm({
                type: this.invoice.type,
                notes: this.invoice.notes,
                user_id: this.selectedUser.id
            }, ({reference}) => {
                this.displayMakeSureModal = false
                if(!this.selectedUser.id) {
                    this.use_rewards({'currency': this.transfer.from_currency, 'total': this.transfer.referral_earnings})
                    this.set_menu_badge({
                        name: 'transfers.history',
                        value: this.currentUser.awaiting_orders + 1,
                        color: 'purple'
                    })
                    this.set_menu_badge({
                        name: 'users.rewards',
                        value: this.currentUser.rewards.unused.filter(reward => reward.total > 0).length,
                        color: 'purple'
                    })
                }
                this.selectedUser.id = undefined
                this.loading = false
                this.$q.notify({
                    position: 'top',
                    icon: 'fa fa-check',
                    message: "Transfer order successfully submitted.",
                    color: 'positive',
                    textColor: 'white',
                })
                this.$router.push({ name: 'transfers.pay', query: {ref: reference}})
            }).catch((response) => {
                switch(response.code) {
                    case 412:
                        this.$q.notify({
                            type: 'warning',
                            message: response.data.error,
                            caption: 'Your exchange rate is older than 30 minutes. Please start over.',
                        })
                        break
                    default:
                        this.$q.notify({
                            type: 'warning',
                            message: 'An unknown error has occurred!',
                        })
                }

                this.step = 1
                this.loading = false
                this.selectedUser.id = undefined
                this.displayMakeSureModal = false
            })
        },
        filterUsers(val, update, abort) {
            if (val.length < 3) {
                abort()
                return
            }

            User.list(`q=${val}`).then(response => {
                this.stringOptions = response
                update(() => {
                    this.users = this.stringOptions
                })
            }).catch(data => {
                abort()
            })
        },
        gotoOnBoarding() {
            if (this.currentUser.set && !this.currentUser.google_form_viewed) {
                this.$router.push({name: 'onboarding'})
            }
        },
        ignoreError() {
            this.errored = false
            this.calculationError.clear()
        },
        stepChanged(currentStep , oldStep) {
            if (currentStep === 2) {
                this.showRecipientDialog = this.filtered_recipients.length === 1 && !this.recipient_loading && !this.loading
                if (this.filtered_recipients.length > 1 && !this.$route.params.baid) {
                    this.$refs.recipient.showPopup()
                } else if (this.$route.params.currency === this.invoice.to_currency) {
                    this.invoice.bank_account_id = this.$route.params.baid
                }
            }
            this.renderKey += 1
        },
        addressProofUpdated(value) {
            if (value.success) {
                this.currentUser.address_age = 0
            }
        }
    },
    created() {
        this.gotoOnBoarding()

        if (this.step === 1) {
            this.fetchRecipients()
        }

        // This dialog is blocking user flow. We must redesign it.
        // Currently, it's set to 3 years.
        if (this.currentUser.address_age >= 1095) {
            this.displayAddressProofDialog = true
        }
    },
    watch: {
        isUserSet(val) {
            if (val) {
                this.gotoOnBoarding()
            }
        }
    },
    components: {
        AddressProofDialog,
        OrderLimitDialog,
        GuideModal,
        TransferLimitErrorModal,
        MakeSureModal,
        EditRecipientModal,
        OpenOrderModal,
        Calculator,
        TransferTime,
        ReviewAmount,
        RecipientModal,
        ReviewRecipient,
        PaymentInfo,
        Alert,
    },
}
</script>
<style>
li {
    padding: 4px;
}
</style>
